import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getCurrencySymbol } from 'projectConstants';
import { clearDataTableDraftRows, getDataTableDraftRows } from 'redux/BuilderModeSlice';

import {
  buildDimensionalDataTableItemRowV2,
  buildTree,
  getDimensionalRowsV2,
} from '../TableHelpersOpzV2';

import mergeDataTableSourceRows from './mergeDataTableSourceRows';

import type { BlockMetadataIndicator } from 'types/builderBlockMetadata';

function findSingleChangedMetadataIndex(
  previous: BlockMetadataIndicator[] | null | undefined,
  next: BlockMetadataIndicator[] | undefined,
): number {
  if (!previous || !next || previous.length !== next.length) return -2;
  const changedIndices = next
    .map((indicator, index) => (indicator !== previous[index] ? index : -1))
    .filter((index) => index !== -1);
  if (changedIndices.length === 0) return -1;
  if (changedIndices.length > 1) return -2;
  return changedIndices[0];
}

/** How many rows were appended at the end (parent + dim children share one prefix). */
function getSuffixAppendCount(previous: any[] | null, next: any[]): number {
  if (!previous?.length || next.length <= previous.length) return 0;
  const prefixMatches = previous.every((row, index) => row === next[index]);
  return prefixMatches ? next.length - previous.length : 0;
}

function findSingleChangedOutputIndex(
  previous: any[] | null | undefined,
  next: any[] | undefined,
): number {
  if (!previous || !next || previous.length !== next.length) return -2;
  const changedIndices = next
    .map((row, index) => (row !== previous[index] ? index : -1))
    .filter((index) => index !== -1);
  if (changedIndices.length === 0) return -1;
  if (changedIndices.length > 1) return -2;
  return changedIndices[0];
}

/** Fingerprint of column headers used for forecast/actuals shading (isActual). */
function getIndicatorHeadersActualsKey(headers: any[] | null | undefined): string {
  if (!headers?.length) return '';
  return headers.map((header) => `${header?.name ?? ''}:${header?.isActual ? 1 : 0}`).join('|');
}

type UseDataTableRowsV2Arguments = {
  indicatorValues: any[] | null | undefined;
  metadataIndicators: BlockMetadataIndicator[];
  indicatorHeaders: any[];
  modelReportingCurrency?: string;
  valuesLoading: boolean;
  blockId: string | undefined;
  scenarioId: number | undefined;
  includeFooterRow?: boolean;
};

/**
 * Server rows from metadata/V2 outputs + draft rows in Redux.
 * Rebuilds only the changed grid row when a single indicator is added (no full refetch).
 */
export default function useDataTableRowsV2({
  indicatorValues,
  metadataIndicators,
  indicatorHeaders,
  modelReportingCurrency,
  valuesLoading,
  blockId,
  scenarioId,
  includeFooterRow = true,
}: UseDataTableRowsV2Arguments) {
  const dispatch = useDispatch();
  const draftRows = useSelector((state: any) => getDataTableDraftRows(state));

  const previousMetadataReference = useRef<BlockMetadataIndicator[] | null>(null);
  const previousOutputsReference = useRef<any[] | null>(null);
  const previousRowsReference = useRef<any[] | null>(null);
  const previousDraftKeyReference = useRef<string>('');
  const previousBlockScenarioKeyReference = useRef<string>('');
  const previousIndicatorHeadersKeyReference = useRef<string>('');

  const blockScenarioKey = `${blockId ?? ''}-${scenarioId ?? ''}`;
  if (previousBlockScenarioKeyReference.current !== blockScenarioKey) {
    previousBlockScenarioKeyReference.current = blockScenarioKey;
    previousMetadataReference.current = null;
    previousOutputsReference.current = null;
    previousRowsReference.current = null;
    previousDraftKeyReference.current = '';
    previousIndicatorHeadersKeyReference.current = '';
  }

  useEffect(() => {
    dispatch(clearDataTableDraftRows());
  }, [blockId, scenarioId, dispatch]);

  const currencySymbol = useMemo(
    () => getCurrencySymbol(modelReportingCurrency),
    [modelReportingCurrency],
  );

  const serverTableData = useMemo(
    () => mergeDataTableSourceRows(indicatorValues, metadataIndicators, indicatorHeaders),
    [indicatorValues, metadataIndicators, indicatorHeaders],
  );

  const draftKey = useMemo(
    () => draftRows.map((row: { id?: string | number }) => String(row.id)).join('|'),
    [draftRows],
  );

  const tableData = useMemo(() => [...serverTableData, ...draftRows], [serverTableData, draftRows]);

  const rows = useMemo(() => {
    const previousMetadata = previousMetadataReference.current;
    const previousOutputs = previousOutputsReference.current;
    const previousRows = previousRowsReference.current;
    const previousDraftKey = previousDraftKeyReference.current;
    const indicatorHeadersKey = getIndicatorHeadersActualsKey(indicatorHeaders);
    const indicatorHeadersUnchanged =
      indicatorHeadersKey === previousIndicatorHeadersKeyReference.current;

    const draftsUnchanged = draftKey === previousDraftKey;
    const rowCountMatches =
      previousRows !== null &&
      previousRows.length === tableData.length + (includeFooterRow ? 2 : 1) &&
      indicatorHeaders?.length > 0;

    const metadataChangedIndex = findSingleChangedMetadataIndex(
      previousMetadata,
      metadataIndicators,
    );
    const suffixAppendCount = getSuffixAppendCount(previousOutputs, indicatorValues ?? []);
    const outputChangedIndex = findSingleChangedOutputIndex(previousOutputs, indicatorValues ?? []);

    if (
      outputChangedIndex >= 0 &&
      suffixAppendCount === 0 &&
      draftsUnchanged &&
      indicatorHeadersUnchanged &&
      rowCountMatches &&
      previousRows &&
      indicatorValues?.length
    ) {
      const nextRows = previousRows.slice();
      nextRows[1 + outputChangedIndex] = buildDimensionalDataTableItemRowV2(
        tableData[outputChangedIndex],
        indicatorHeaders,
        tableData.length,
        outputChangedIndex,
        currencySymbol,
        valuesLoading,
      );
      const withTree = buildTree(nextRows);
      previousMetadataReference.current = metadataIndicators;
      previousOutputsReference.current = indicatorValues ?? [];
      previousRowsReference.current = withTree;
      previousDraftKeyReference.current = draftKey;
      previousIndicatorHeadersKeyReference.current = indicatorHeadersKey;
      return withTree;
    }

    if (
      suffixAppendCount > 0 &&
      draftsUnchanged &&
      indicatorHeadersUnchanged &&
      rowCountMatches &&
      previousRows &&
      tableData.length >= suffixAppendCount
    ) {
      const nextRows = previousRows.slice();
      const insertAt = includeFooterRow ? nextRows.length - 1 : nextRows.length;
      const startDataIndex = tableData.length - suffixAppendCount;

      for (let offset = 0; offset < suffixAppendCount; offset += 1) {
        const dataIndex = startDataIndex + offset;
        nextRows.splice(
          insertAt + offset,
          0,
          buildDimensionalDataTableItemRowV2(
            tableData[dataIndex],
            indicatorHeaders,
            tableData.length,
            dataIndex,
            currencySymbol,
            valuesLoading,
          ),
        );
      }

      const withTree = buildTree(nextRows);
      previousMetadataReference.current = metadataIndicators;
      previousOutputsReference.current = indicatorValues ?? [];
      previousRowsReference.current = withTree;
      previousDraftKeyReference.current = draftKey;
      previousIndicatorHeadersKeyReference.current = indicatorHeadersKey;
      return withTree;
    }

    if (
      metadataChangedIndex >= 0 &&
      outputChangedIndex < 0 &&
      suffixAppendCount === 0 &&
      draftsUnchanged &&
      indicatorHeadersUnchanged &&
      rowCountMatches &&
      previousRows
    ) {
      const nextRows = previousRows.slice();
      nextRows[1 + metadataChangedIndex] = buildDimensionalDataTableItemRowV2(
        tableData[metadataChangedIndex],
        indicatorHeaders,
        tableData.length,
        metadataChangedIndex,
        currencySymbol,
        valuesLoading,
      );
      const withTree = buildTree(nextRows);
      previousMetadataReference.current = metadataIndicators;
      previousOutputsReference.current = indicatorValues ?? [];
      previousRowsReference.current = withTree;
      previousDraftKeyReference.current = draftKey;
      previousIndicatorHeadersKeyReference.current = indicatorHeadersKey;
      return withTree;
    }

    if (
      metadataChangedIndex === -1 &&
      outputChangedIndex === -1 &&
      suffixAppendCount === 0 &&
      draftsUnchanged &&
      indicatorHeadersUnchanged &&
      rowCountMatches &&
      previousRows
    ) {
      previousMetadataReference.current = metadataIndicators;
      previousOutputsReference.current = indicatorValues ?? [];
      previousDraftKeyReference.current = draftKey;
      previousIndicatorHeadersKeyReference.current = indicatorHeadersKey;
      return previousRows;
    }

    if (tableData.length === 0 || !indicatorHeaders?.length) {
      previousMetadataReference.current = metadataIndicators;
      previousOutputsReference.current = indicatorValues ?? [];
      previousRowsReference.current = [];
      previousDraftKeyReference.current = draftKey;
      previousIndicatorHeadersKeyReference.current = indicatorHeadersKey;
      return [];
    }

    const processed = getDimensionalRowsV2(
      tableData,
      indicatorHeaders,
      currencySymbol,
      valuesLoading,
      { includeFooterRow },
    );
    const fullRows = buildTree(processed);
    previousMetadataReference.current = metadataIndicators;
    previousOutputsReference.current = indicatorValues ?? [];
    previousRowsReference.current = fullRows;
    previousDraftKeyReference.current = draftKey;
    previousIndicatorHeadersKeyReference.current = indicatorHeadersKey;
    return fullRows;
  }, [
    tableData,
    metadataIndicators,
    indicatorValues,
    indicatorHeaders,
    valuesLoading,
    currencySymbol,
    draftKey,
    includeFooterRow,
  ]);

  return { tableData, rows };
}
